import { useEffect, useState } from "react";
import { Lead } from "../types";
import { apiFetch } from "../services/api";
export default function ContactDrawer({
  lead,
  onClose,
  onUnlock,
  publicMode = false,
}: {
  lead: Lead;
  onClose: () => void;
  onUnlock: (lead: Lead) => void;
  publicMode?: boolean;
}) {
  const [details, setDetails] = useState<Record<string,string>>(lead.details || {});
  useEffect(() => {
    setDetails(lead.details || {});
    if (!lead.unlocked || publicMode) return;
    let active = true;
    apiFetch(`/api/leads/${lead.id}`).then(async r => {
      if (r.ok && active) setDetails((await r.json()).details || {});
    }).catch(() => {});
    return () => {active = false;};
  }, [lead.id,lead.unlocked,publicMode]);
  const labels: Record<string,string> = {domain:'Company join domain',searched_domain:'Searched domain',email_domain:'Contact email domain',final_email_domain:'Final email domain',phone_type:'Phone type',email_verification_date:'Email verification date',revenue:'Revenue',company_phone:'Company phone',company_linkedin_url:'Company LinkedIn',address:'Address',zip:'Postal code',services:'Services',home_page_email:'Company homepage email'};
  const [note, setNote] = useState(""),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false);
  useEffect(() => {
    const handle = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handle);
    return () => document.removeEventListener("keydown", handle);
  }, [onClose]);
  useEffect(() => {
    if (publicMode) return;
    apiFetch(`/api/workspace/notes/${lead.id}`)
      .then(async (r) => {
        if (r.ok) setNote((await r.json()).note);
      })
      .catch(() => setMessage("Unable to load notes."));
  }, [lead.id, publicMode]);
  const save = async () => {
    setBusy(true);
    try {
      const r = await apiFetch(`/api/workspace/notes/${lead.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ note }),
      });
      if (!r.ok) throw new Error();
      setMessage("Note saved.");
    } catch {
      setMessage("Unable to save your note.");
    }
    setBusy(false);
  };
  const safeLink = (url?: string) =>
    url && /^https?:\/\//i.test(url) ? url : undefined;
  return (
    <div className="drawer-overlay" onClick={onClose}>
      <aside
        className="contact-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Contact details"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="icon-button drawer-close"
          onClick={onClose}
          aria-label="Close contact details"
          autoFocus
        >
          ×
        </button>
        <span className="contact-avatar large">
          {lead.first_name[0]}
          {lead.last_name[0]}
        </span>
        <h2>
          {lead.first_name} {lead.last_name}
        </h2>
        <p>
          {lead.job_title || "Business contact"} at{" "}
          <strong>{lead.company}</strong>
        </p>
        <span className="muted">
          {[lead.city, lead.country].filter(Boolean).join(", ") ||
            "Location not provided"}
        </span>
        <section className="drawer-section">
          <span className="eyebrow">CONTACT DETAILS</span>
          <div className="detail-row">
            <span>Email</span>
            <strong>{lead.email || "Hidden until unlocked"}</strong>
          </div>
          <div className="detail-row">
            <span>Phone</span>
            <strong>
              {lead.unlocked
                ? lead.phone || "Not provided"
                : lead.phone_available
                  ? "Available · locked"
                  : "Not provided"}
            </strong>
          </div>
          {lead.unlocked && safeLink(lead.linkedin_url) && (
            <a href={lead.linkedin_url} target="_blank" rel="noreferrer">
              LinkedIn profile ↗
            </a>
          )}
          {lead.unlocked && safeLink(lead.website) && (
            <a href={lead.website} target="_blank" rel="noreferrer">
              Company website ↗
            </a>
          )}
          {!lead.unlocked && (
            <button
              className="button button-dark full"
              onClick={() => onUnlock(lead)}
            >
              Unlock contact · 1 credit
            </button>
          )}
        </section>
        <section className="drawer-section">
          <span className="eyebrow">COMPANY & DATA</span>
          <div className="detail-row">
            <span>Industry</span>
            <strong>{lead.industry || "Not provided"}</strong>
          </div>
          <div className="detail-row">
            <span>Company size</span>
            <strong>{lead.company_size || "Not provided"}</strong>
          </div>
          <div className="detail-row">
            <span>Imported email status</span>
            <strong className="data-badge">{lead.verification_badge}</strong>
          </div>
          <p className="fine-print">
            Status supplied with the record; this is not a live email
            verification. Last record update:{" "}
            {lead.updated_at
              ? new Date(lead.updated_at + "Z").toLocaleDateString()
              : "Not available"}
            .
          </p>
        </section>
        {lead.unlocked && Object.keys(details).length > 0 && (
          <section className="drawer-section">
            <span className="eyebrow">MORE RECORD DETAILS</span>
            {Object.entries(labels).filter(([key]) => details[key]).map(([key,label]) => (
              <div className="detail-row" key={key}><span>{label}</span><strong>{details[key]}</strong></div>
            ))}
            {['home_page_text','linkedin_bio'].filter(key=>details[key]).map(key=>(
              <details key={key}><summary>{key === 'home_page_text' ? 'Company description' : 'Company LinkedIn bio'}</summary><p style={{whiteSpace:'pre-wrap',overflowWrap:'anywhere'}}>{details[key]}</p></details>
            ))}
          </section>
        )}
        {!publicMode && (
          <section className="drawer-section">
            <label className="eyebrow" htmlFor="private-note">
              YOUR PRIVATE NOTE
            </label>
            <textarea
              id="private-note"
              maxLength={4000}
              rows={5}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Keep context for your next conversation…"
            />
            <button
              className="button button-outline"
              disabled={busy}
              onClick={() => void save()}
            >
              Save note
            </button>
            {message && (
              <p role="status" className="fine-print">
                {message}
              </p>
            )}
          </section>
        )}
      </aside>
    </div>
  );
}
